import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Home, Presentation, Clock } from 'lucide-react';
import { lessonService } from '../../services/lessonService';
import { Lesson, TimelineStep } from '../../types';
import { htmlToText } from '../../utils/htmlToPowerPoint';
import { useTranslation } from '../../contexts/TranslationContext';

type SlideKind =
  | 'context'
  | 'teacher'
  | 'students'
  | 'practice'
  | 'assessment'
  | 'differentiation';

interface LessonSlide {
  id: string;
  stepIndex: number;
  stepName: string;
  kind: SlideKind;
  title: string;
  subtitle?: string;
  bullets: string[];
}

function toBullets(values: string[] = []): string[] {
  return values
    .map(v => htmlToText(v || '').trim())
    .filter(Boolean);
}

function buildSlides(lesson: Lesson, t: (key: string, params?: Record<string, any>) => string): LessonSlide[] {
  const slides: LessonSlide[] = [];
  const stepTypeLabels: Record<string, string> = {
    warmup: t('lessonSlides.stepTypes.warmup'),
    launch: t('lessonSlides.stepTypes.launch'),
    direct_instruction: t('lessonSlides.stepTypes.directInstruction'),
    guided_practice: t('lessonSlides.stepTypes.guidedPractice'),
    independent_practice: t('lessonSlides.stepTypes.independentPractice'),
    closing: t('lessonSlides.stepTypes.closing'),
  };

  lesson.timeline.forEach((step: TimelineStep, stepIndex: number) => {
    const stepName = step.name || t('lessonSlides.stepFallback', { n: stepIndex + 1 });
    const stepType = stepTypeLabels[step.step_type] || step.step_type;

    slides.push({
      id: `${step.id}-context`,
      stepIndex,
      stepName,
      kind: 'context',
      title: t('lessonSlides.sessionTitle', { n: stepIndex + 1, name: stepName }),
      subtitle: `${stepType} • ${step.minutes || 0} ${t('lessonList.min')}`,
      bullets: [
        t('lessonSlides.instructionalFlow'),
        t('lessonSlides.goalLine'),
      ],
    });

    const teacherBullets = toBullets(step.teacher_script || []);
    if (teacherBullets.length) {
      slides.push({
        id: `${step.id}-teacher`,
        stepIndex,
        stepName,
        kind: 'teacher',
        title: t('lessonSlides.iDoTitle'),
        subtitle: t('lessonSlides.iDoSubtitle'),
        bullets: teacherBullets,
      });
    }

    const studentBullets = toBullets(step.student_actions || []);
    if (studentBullets.length) {
      slides.push({
        id: `${step.id}-students`,
        stepIndex,
        stepName,
        kind: 'students',
        title: t('lessonSlides.weYouTitle'),
        subtitle: t('lessonSlides.weYouSubtitle'),
        bullets: studentBullets,
      });
    }

    slides.push({
      id: `${step.id}-practice`,
      stepIndex,
      stepName,
      kind: 'practice',
      title: t('lessonSlides.practiceTitle'),
      subtitle: t('lessonSlides.practiceSubtitle'),
      bullets: studentBullets.length
        ? studentBullets.slice(0, 8)
        : [
            t('lessonSlides.practiceFallback1'),
            t('lessonSlides.practiceFallback2'),
            t('lessonSlides.practiceFallback3'),
          ],
    });

    if (step.checks_for_understanding?.length) {
      slides.push({
        id: `${step.id}-assessment`,
        stepIndex,
        stepName,
        kind: 'assessment',
        title: t('lessonSlides.assessmentTitle'),
        subtitle: t('lessonSlides.assessmentSubtitle'),
        bullets: step.checks_for_understanding.map((check, index) => {
          const expected = Array.isArray(check.expected)
            ? check.expected.join(', ')
            : check.expected || '';
          return [
            t('lessonSlides.checkLine', { n: index + 1, type: check.type || t('lessonSlides.formativeType') }),
            check.prompt ? `${t('lessonSlides.promptLabel')}: ${check.prompt}` : '',
            check.look_for ? `${t('lessonSlides.lookForLabel')}: ${check.look_for}` : '',
            expected ? `${t('lessonSlides.expectedLabel')}: ${expected}` : '',
          ]
            .filter(Boolean)
            .join(' | ');
        }),
      });
    }

    const diffBullets: string[] = [];
    if (step.differentiation?.supports) {
      Object.entries(step.differentiation.supports).forEach(([k, v]) => {
        diffBullets.push(`${t('lessonSlides.supportLabel')} (${k}): ${v}`);
      });
    }
    if (step.differentiation?.extensions) {
      Object.entries(step.differentiation.extensions).forEach(([k, v]) => {
        diffBullets.push(`${t('lessonSlides.extensionLabel')} (${k}): ${v}`);
      });
    }
    if (step.adaptivity?.rule) {
      diffBullets.push(`${t('lessonSlides.adaptivityLabel')}: ${step.adaptivity.rule}`);
    }
    if (diffBullets.length) {
      slides.push({
        id: `${step.id}-differentiation`,
        stepIndex,
        stepName,
        kind: 'differentiation',
        title: t('lessonSlides.diffTitle'),
        subtitle: t('lessonSlides.diffSubtitle'),
        bullets: diffBullets,
      });
    }
  });

  return slides;
}

export const LessonSlideView: React.FC = () => {
  const { t } = useTranslation();
  const { lessonId } = useParams<{ lessonId: string }>();
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const run = async () => {
      if (!lessonId) return;
      setLoading(true);
      const data = await lessonService.getLessonById(lessonId);
      setLesson(data);
      setLoading(false);
    };
    run();
  }, [lessonId]);

  const slides = useMemo(() => (lesson ? buildSlides(lesson, t) : []), [lesson, t]);
  const currentSlide = slides[current];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!slides.length) return;
      if (e.key === 'ArrowRight') setCurrent(prev => Math.min(prev + 1, slides.length - 1));
      if (e.key === 'ArrowLeft') setCurrent(prev => Math.max(prev - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slides.length]);

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <div className="text-gray-600">{t('lessonSlides.loading')}</div>
      </div>
    );
  }

  if (!lesson || !slides.length || !currentSlide) {
    return (
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('lessonSlides.unavailableTitle')}</h2>
        <p className="text-gray-600 mb-4">{t('lessonSlides.unavailableDescription')}</p>
        <Link to="/lessons" className="btn-primary inline-flex items-center space-x-2">
          <Home className="w-4 h-4" />
          <span>{t('lessonSlides.backToLessons')}</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="card flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Presentation className="w-5 h-5 text-blue-600" />
            {lesson.metadata.title}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {t('lessonSlides.progress', {
              slide: current + 1,
              total: slides.length,
              step: currentSlide.stepIndex + 1,
              name: currentSlide.stepName,
            })}
          </p>
        </div>
        <Link to="/lessons" className="btn-secondary inline-flex items-center space-x-2">
          <Home className="w-4 h-4" />
          <span>{t('lessonSlides.lessons')}</span>
        </Link>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 min-h-[60vh]">
        <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
          {currentSlide.kind}
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">{currentSlide.title}</h2>
        {currentSlide.subtitle && (
          <p className="text-gray-600 mb-6 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            {currentSlide.subtitle}
          </p>
        )}
        <div className="space-y-3">
          {currentSlide.bullets.map((bullet, index) => (
            <div key={index} className="text-lg text-gray-800 leading-relaxed">
              {index + 1}. {bullet}
            </div>
          ))}
        </div>
      </div>

      <div className="card flex items-center justify-between">
        <button
          onClick={() => setCurrent(prev => Math.max(prev - 1, 0))}
          disabled={current === 0}
          className="btn-secondary inline-flex items-center space-x-2 disabled:opacity-50"
        >
          <ChevronLeft className="w-4 h-4" />
          <span>{t('actions.previous')}</span>
        </button>
        <div className="text-sm text-gray-500">{t('lessonSlides.arrowsHint')}</div>
        <button
          onClick={() => setCurrent(prev => Math.min(prev + 1, slides.length - 1))}
          disabled={current === slides.length - 1}
          className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
        >
          <span>{t('actions.next')}</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

